/**
 * ABRO Sticky Add-to-Cart (mobile-only)
 *
 * Discovers the page's existing product submit button automatically
 * (id starts with "ProductSubmitButton-") and proxies clicks to it.
 *
 * Notes:
 *  - Doesn't render its own form. Shopify's <product-form> custom
 *    element handles everything including variant changes.
 *  - No URL construction, no extra fetches, no theme files modified.
 *  - Bails out completely on viewports >= 750px so desktop pays no
 *    cost beyond a single matchMedia check.
 */

(function () {
  'use strict';

  if (customElements.get('abro-sticky-atc')) return;

  const MOBILE_QUERY = '(max-width: 749px)';
  const SOURCE_SELECTOR = '[id^="ProductSubmitButton-"]';

  class AbroStickyATC extends HTMLElement {
    constructor() {
      super();
      this._mql = null;
      this._io = null;
      this._mo = null;
      this._sourceBtn = null;
      this._proxyBtn = null;
      this._labelEl = null;
      this._enabled = false;
      this._onResize = this._onResize.bind(this);
      this._onClick = this._onClick.bind(this);
      this._syncFromSource = this._syncFromSource.bind(this);
    }

    connectedCallback() {
      this._mql =
        typeof window.matchMedia === 'function' ? window.matchMedia(MOBILE_QUERY) : null;

      this._proxyBtn = this.querySelector('[data-abro-sticky-submit]');
      this._labelEl = this.querySelector('[data-abro-sticky-label]');
      if (!this._proxyBtn) return;

      this._findSourceButton(() => this._init());
    }

    disconnectedCallback() {
      this._teardown();
    }

    /**
     * Find the product page's main submit button. Try immediately, then
     * fall back to a few short retries — the button is rendered server-side
     * so it's almost always present, but we play safe in case our section
     * is rendered before <main-product> in some custom layout.
     */
    _findSourceButton(done) {
      const find = () => document.querySelector(SOURCE_SELECTOR);
      let btn = find();
      if (btn) {
        this._sourceBtn = btn;
        done();
        return;
      }
      let tries = 0;
      const max = 10;
      const tick = () => {
        tries += 1;
        btn = find();
        if (btn || tries >= max) {
          this._sourceBtn = btn;
          done();
        } else {
          setTimeout(tick, 100);
        }
      };
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', tick, { once: true });
      } else {
        setTimeout(tick, 100);
      }
    }

    _init() {
      if (!this._sourceBtn) return; // nothing to attach to (e.g. preview / non-product page)

      if (this._mql) {
        if (typeof this._mql.addEventListener === 'function') {
          this._mql.addEventListener('change', this._onResize);
        } else if (typeof this._mql.addListener === 'function') {
          this._mql.addListener(this._onResize);
        }
      }

      this._proxyBtn.addEventListener('click', this._onClick);

      this._mo = new MutationObserver(this._syncFromSource);
      this._mo.observe(this._sourceBtn, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled'],
        childList: true,
        subtree: true,
        characterData: true,
      });
      this._syncFromSource();
      this._applyMode();
    }

    _onResize() { this._applyMode(); }

    _applyMode() {
      const isMobile = this._mql ? this._mql.matches : window.innerWidth <= 749;
      if (isMobile && !this._enabled) this._enable();
      else if (!isMobile && this._enabled) this._disable();
    }

    _enable() {
      if (!this._sourceBtn) return;
      this._enabled = true;
      document.body.classList.add('abro-sticky-atc-active');
      this._io = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const sourceVisible = entry.isIntersecting;
            this.classList.toggle('is-visible', !sourceVisible);
            this.setAttribute('aria-hidden', sourceVisible ? 'true' : 'false');
          }
        },
        { root: null, threshold: 0.01 }
      );
      this._io.observe(this._sourceBtn);
    }

    _disable() {
      this._enabled = false;
      document.body.classList.remove('abro-sticky-atc-active');
      this.classList.remove('is-visible');
      this.setAttribute('aria-hidden', 'true');
      if (this._io) {
        this._io.disconnect();
        this._io = null;
      }
    }

    _syncFromSource() {
      if (!this._sourceBtn || !this._proxyBtn) return;
      const isDisabled =
        this._sourceBtn.hasAttribute('disabled') ||
        this._sourceBtn.getAttribute('aria-disabled') === 'true';
      if (isDisabled) this._proxyBtn.setAttribute('disabled', '');
      else this._proxyBtn.removeAttribute('disabled');
      const sourceLabel = this._sourceBtn.querySelector('span');
      if (sourceLabel && this._labelEl) {
        const text = (sourceLabel.textContent || '').trim();
        if (text) this._labelEl.textContent = text;
      }
    }

    _onClick(e) {
      e.preventDefault();
      if (!this._sourceBtn) return;
      if (
        this._sourceBtn.hasAttribute('disabled') ||
        this._sourceBtn.getAttribute('aria-disabled') === 'true'
      ) return;
      this._sourceBtn.click();
    }

    _teardown() {
      if (this._io) { this._io.disconnect(); this._io = null; }
      if (this._mo) { this._mo.disconnect(); this._mo = null; }
      if (this._mql) {
        if (typeof this._mql.removeEventListener === 'function') {
          this._mql.removeEventListener('change', this._onResize);
        } else if (typeof this._mql.removeListener === 'function') {
          this._mql.removeListener(this._onResize);
        }
      }
      if (this._proxyBtn) this._proxyBtn.removeEventListener('click', this._onClick);
      document.body.classList.remove('abro-sticky-atc-active');
    }
  }

  customElements.define('abro-sticky-atc', AbroStickyATC);
})();
